import { useState, useEffect } from "react"
import { Building2, Briefcase, Filter, MapPin, Clock, Users, X } from "lucide-react"
import { useNavigate } from "react-router-dom"
import Header from "../components/Header"
import Footer from "../components/Footer"
import FilterSidebar from "../components/FilterSidebar"

const FONT = '"Plus Jakarta Sans", sans-serif'

const JobListingsPage = () => {
  const navigate = useNavigate()

  const [jobListings, setJobListings] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  const [selectedWorkMode, setSelectedWorkMode] = useState({ Hybrid: false, Remote: true, "In-office": false })
  const [selectedLocations, setSelectedLocations] = useState({ Bangalore: false, Hyderabad: false, Gurgaon: false, Noida: false, Pune: false })
  const [minSalary, setMinSalary] = useState("")

  useEffect(() => {
    const fetchJobListings = async () => {
      try {
        setIsLoading(true)
        const response = await fetch("https://hire100x-e1dd4.uc.r.appspot.com/jobListings", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        })
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
        const result = await response.json()
        if (result.success && result.data) setJobListings(result.data)
        else throw new Error(result.message || "Failed to fetch job listings")
      } catch (err) {
        setError("Failed to load job listings. Please try again later.")
      } finally {
        setIsLoading(false)
      }
    }
    fetchJobListings()
  }, [])

  const filteredJobListings = jobListings.filter((job) => {
    const selectedLocationsList = Object.entries(selectedLocations)
      .filter(([, selected]) => selected)
      .map(([location]) => location)
    if (selectedLocationsList.length > 0 && !selectedLocationsList.includes(job.location)) return false
    if (minSalary && job.salary && job.salary < parseInt(minSalary)) return false
    return true
  })

  const clearFilters = () => {
    setSelectedWorkMode({ Hybrid: false, Remote: true, "In-office": false })
    setSelectedLocations({ Bangalore: false, Hyderabad: false, Gurgaon: false, Noida: false, Pune: false })
    setMinSalary("")
  }

  const stripHtmlTags = (html) => {
    if (!html) return ""
    const doc = new DOMParser().parseFromString(html, "text/html")
    return doc.body.textContent || ""
  }

  const getRelativeTime = (timestamp) => {
    if (!timestamp) return "4 days ago"
    const date = new Date(timestamp._seconds * 1000)
    const diffInDays = Math.floor((Date.now() - date) / 86400000)
    if (diffInDays === 0) return "Today"
    if (diffInDays === 1) return "Yesterday"
    if (diffInDays < 7) return `${diffInDays} days ago`
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`
    return `${Math.floor(diffInDays / 30)} months ago`
  }

  const activeFilterCount =
    Object.values(selectedWorkMode).filter(Boolean).length +
    Object.values(selectedLocations).filter(Boolean).length +
    (minSalary ? 1 : 0)

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: "#FDFAF8", fontFamily: FONT }}>
        <Header hideFreeTrial />
        <div className="flex-1 flex overflow-hidden pt-20">
          <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl flex gap-6 pt-6">
            {/* Sidebar shimmer */}
            <div className="hidden lg:block flex-shrink-0 w-64">
              <div className="bg-white rounded-xl border border-zinc-200 p-5">
                <div className="h-5 w-24 bg-zinc-100 rounded animate-pulse mb-5" />
                {[1, 2, 3].map(s => (
                  <div key={s} className="mb-5">
                    <div className="h-4 w-20 bg-zinc-100 rounded animate-pulse mb-3" />
                    {[1, 2, 3].map(i => (
                      <div key={i} className="flex items-center gap-3 mb-2.5">
                        <div className="h-4 w-4 bg-zinc-100 rounded animate-pulse" />
                        <div className="h-3.5 flex-1 bg-zinc-100 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            {/* Cards shimmer */}
            <div className="flex-1 space-y-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-xl border border-zinc-200 p-5">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-zinc-100 rounded-lg animate-pulse flex-shrink-0" />
                    <div className="flex-1">
                      <div className="h-5 w-2/3 bg-zinc-100 rounded animate-pulse mb-2" />
                      <div className="h-4 w-1/3 bg-zinc-100 rounded animate-pulse mb-3" />
                      <div className="h-3.5 w-full bg-zinc-100 rounded animate-pulse mb-1.5" />
                      <div className="h-3.5 w-4/5 bg-zinc-100 rounded animate-pulse" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  /* ── Error ── */
  if (error) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: "#FDFAF8", fontFamily: FONT }}>
        <Header hideFreeTrial />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-14 h-14 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-7 h-7 text-zinc-500" />
            </div>
            <h2 className="text-lg font-semibold text-zinc-900 mb-2" style={{ fontFamily: FONT }}>Unable to load listings</h2>
            <p className="text-[14px] text-zinc-500 mb-5" style={{ fontFamily: FONT }}>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-zinc-900 hover:bg-zinc-700 text-white text-[13px] font-semibold rounded-lg transition-colors cursor-pointer"
              style={{ fontFamily: FONT }}
            >
              Try again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#FDFAF8", fontFamily: FONT }}>
      <Header hideFreeTrial />

      {/* Page title strip */}
      <div className="pt-20 pb-0 border-b border-zinc-200" style={{ background: "#FDFAF8" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-zinc-900" style={{ fontFamily: FONT }}>
            Open Roles
          </h1>
          <p className="text-[14px] text-zinc-500 mt-1.5" style={{ fontFamily: FONT }}>
            {filteredJobListings.length} positions available
          </p>
        </div>
      </div>

      {/* Main layout */}
      <div className="flex-1 flex overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-6 pt-6 pb-24 lg:pb-8">

          {/* Desktop sidebar */}
          <div className="hidden lg:block flex-shrink-0">
            <FilterSidebar
              selectedWorkMode={selectedWorkMode}
              setSelectedWorkMode={setSelectedWorkMode}
              selectedLocations={selectedLocations}
              setSelectedLocations={setSelectedLocations}
              minSalary={minSalary}
              setMinSalary={setMinSalary}
              clearFilters={clearFilters}
            />
          </div>

          {/* Mobile filter drawer */}
          {showMobileFilters && (
            <div className="lg:hidden fixed inset-0 z-50 bg-black/40 backdrop-blur-sm">
              <div className="absolute right-0 top-0 h-full w-80 max-w-full bg-white flex flex-col shadow-2xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
                  <h3 className="text-[15px] font-semibold text-zinc-900" style={{ fontFamily: FONT }}>Filters</h3>
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-100 text-zinc-500 transition-colors cursor-pointer"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <FilterSidebar
                    selectedWorkMode={selectedWorkMode}
                    setSelectedWorkMode={setSelectedWorkMode}
                    selectedLocations={selectedLocations}
                    setSelectedLocations={setSelectedLocations}
                    minSalary={minSalary}
                    setMinSalary={setMinSalary}
                    clearFilters={clearFilters}
                    isMobile
                  />
                </div>
                <div className="px-5 py-4 border-t border-zinc-100">
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="w-full py-2.5 bg-zinc-900 hover:bg-zinc-700 text-white text-[13px] font-semibold rounded-lg transition-colors cursor-pointer"
                    style={{ fontFamily: FONT }}
                  >
                    Apply filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Job cards */}
          <div className="flex-1 min-w-0 overflow-y-auto">
            {filteredJobListings.length === 0 ? (
              <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center">
                <Briefcase className="mx-auto h-10 w-10 text-zinc-300 mb-4" />
                <h3 className="text-[15px] font-semibold text-zinc-900 mb-1" style={{ fontFamily: FONT }}>No jobs found</h3>
                <p className="text-[13px] text-zinc-500" style={{ fontFamily: FONT }}>Try adjusting your filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredJobListings.map((job) => (
                  <div
                    key={job.id}
                    onClick={() => navigate(`/jobs/${job.jobListingId || job.id}`)}
                    className="bg-white border border-zinc-200 rounded-xl p-5 cursor-pointer transition-all duration-150 hover:border-zinc-400 hover:shadow-sm"
                  >
                    {/* Desktop */}
                    <div className="hidden sm:flex items-start gap-4">
                      {/* Logo */}
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden border border-zinc-200 bg-zinc-50">
                        {job.organizationLogoUrl ? (
                          <img
                            src={job.organizationLogoUrl}
                            alt={job.organizationName}
                            className="w-full h-full object-contain p-1.5"
                            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex" }}
                          />
                        ) : null}
                        <div className={`w-full h-full bg-zinc-900 rounded-lg flex items-center justify-center ${job.organizationLogoUrl ? "hidden" : "flex"}`}>
                          <Building2 className="w-5 h-5 text-white" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <h3 className="text-[15px] font-semibold text-zinc-900 leading-snug" style={{ fontFamily: FONT }}>
                            {job.role || "Open Position"}
                          </h3>
                          {job.workMode && (
                            <span className="flex-shrink-0 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-zinc-100 text-zinc-600 border border-zinc-200" style={{ fontFamily: FONT }}>
                              {job.workMode}
                            </span>
                          )}
                        </div>

                        <p className="text-[13px] font-medium text-zinc-500 mb-3" style={{ fontFamily: FONT }}>
                          {job.organizationName}
                        </p>

                        <p className="text-[13px] text-zinc-500 line-clamp-2 leading-relaxed mb-4" style={{ fontFamily: FONT }}>
                          {stripHtmlTags(job.requirements) || job.description}
                        </p>

                        {job.required_skills?.length > 0 && (
                          <div className="flex items-center flex-wrap gap-1.5 mb-4">
                            {job.required_skills.slice(0, 4).map((skill, i) => (
                              <span key={i} className="px-2.5 py-0.5 rounded-md text-[11px] font-medium bg-zinc-100 text-zinc-600 border border-zinc-200" style={{ fontFamily: FONT }}>
                                {skill}
                              </span>
                            ))}
                            {job.required_skills.length > 4 && (
                              <span className="text-[11px] text-zinc-400 font-medium" style={{ fontFamily: FONT }}>+{job.required_skills.length - 4} more</span>
                            )}
                          </div>
                        )}

                        <div className="flex items-center flex-wrap gap-4 text-[12px] text-zinc-400" style={{ fontFamily: FONT }}>
                          <span className="flex items-center gap-1.5">
                            <MapPin size={12} />
                            {job.location || "Remote"}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Users size={12} />
                            {job.applicantCount || 0} applicants
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock size={12} />
                            {getRelativeTime(job.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Mobile */}
                    <div className="sm:hidden">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden border border-zinc-200 bg-zinc-50">
                          {job.organizationLogoUrl ? (
                            <img
                              src={job.organizationLogoUrl}
                              alt={job.organizationName}
                              className="w-full h-full object-contain p-1.5"
                              onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex" }}
                            />
                          ) : null}
                          <div className={`w-full h-full bg-zinc-900 rounded-lg flex items-center justify-center ${job.organizationLogoUrl ? "hidden" : "flex"}`}>
                            <Building2 className="w-5 h-5 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-[14px] font-semibold text-zinc-900 leading-snug mb-0.5" style={{ fontFamily: FONT }}>
                            {job.role || "Open Position"}
                          </h3>
                          <p className="text-[12px] text-zinc-500 font-medium" style={{ fontFamily: FONT }}>{job.organizationName}</p>
                        </div>
                        {job.workMode && (
                          <span className="flex-shrink-0 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-zinc-100 text-zinc-600" style={{ fontFamily: FONT }}>
                            {job.workMode}
                          </span>
                        )}
                      </div>

                      <p className="text-[12px] text-zinc-500 line-clamp-3 leading-relaxed mb-3" style={{ fontFamily: FONT }}>
                        {stripHtmlTags(job.requirements) || job.description}
                      </p>

                      {job.required_skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {job.required_skills.slice(0, 3).map((skill, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-zinc-100 text-zinc-600 border border-zinc-200" style={{ fontFamily: FONT }}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center flex-wrap gap-3 text-[11px] text-zinc-400" style={{ fontFamily: FONT }}>
                        <span className="flex items-center gap-1"><MapPin size={11} />{job.location || "Remote"}</span>
                        <span className="flex items-center gap-1"><Users size={11} />{job.applicantCount || 0} applicants</span>
                        <span className="flex items-center gap-1"><Clock size={11} />{getRelativeTime(job.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter FAB */}
      <div className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
        <button
          onClick={() => setShowMobileFilters(true)}
          className="flex items-center gap-2 bg-zinc-900 hover:bg-zinc-700 text-white px-5 py-3 rounded-full shadow-xl transition-colors cursor-pointer text-[13px] font-semibold"
          style={{ fontFamily: FONT }}
        >
          <Filter size={15} />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-white text-zinc-900 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <Footer />
    </div>
  )
}

export default JobListingsPage
